import { msg } from "@omegagrid/localize";
import { MenuItem, dates } from "@omegagrid/core";
import { NodeModel, NodeSource, TreeOptions, TreeSource } from "@omegagrid/tree";
import { Thread } from "@reporting/rp-types/etl";
import rp, { BrowserAdapter } from "@reporting/rp-app-host";
import { html, css, render } from "lit";
import { EntityAccessType } from "@reporting/rp-types/users";
import { SortMode, buildFolderTree, makeSiblingComparator, walkTree } from "../folderTree";

const formatDate = (d: string) => dates.format(new Date(d), dates.getDateFormat() + ' HH:mm');

export class ThreadBrowserAdapter extends BrowserAdapter {

	get etlClient() { return rp.services.get('etl') }
	get host() { return rp.host }

	async getTree(): Promise<TreeSource> {
		const mode: SortMode = rp.settings.get('etl.threads.sorting') as SortMode || 'description/ord_num';
		const compare = makeSiblingComparator<Thread>(mode, t => t.ord_num || 0);

		const threadTypes = (await this.etlClient.get<{k: string, v: string}[]>(`threadTypes`));
		const threads = (await this.etlClient.get<Thread[]>(`threads`, {
			fields: ['id_thread', 'description', 'type', 'enabled', 'ord_num', 'date_created', 'who_created_dv', 'accounting', 'sync', 'app_lock'],
			order: [['type', 'asc'], ['description', 'asc'], ['ord_num', 'asc']],
			includeShared: true
		})).reduce<Record<string, Thread[]>>((acc, item) => {
			// Threads with no type fall into the Unknown group
			const type = item.type || '';
			acc[type] = acc[type] || [];
			acc[type].push(item);
			return acc;
		}, {});

		return threadTypes.flatMap(type => {
			const threadsOfType = threads[type.k];
			if (!threadsOfType || threadsOfType.length === 0) return [];

			const root = buildFolderTree(threadsOfType, t => t.description, t => t.ord_num || 0);
			const children: NodeSource[] = [];
			walkTree(root, compare, {
				folder: (folder, level) => children.push({
					k: type.k + '-' + folder.name,
					v: folder.name,
					l: level,
					collapsed: true,
					folder: BrowserAdapter.folderOptions,
					props: { path: folder.pathChunks, dir: true }
				}),
				file: (leaf, parent, level) => {
					const fileConfig = this.getFileConfigByName(leaf.filename) ?? BrowserAdapter.fileConfig.get('sql');
					children.push({
						k: `etl-thread-${leaf.item.id_thread}`,
						v: leaf.filename,
						l: level,
						icon: fileConfig.icon,
						props: { thread: leaf.item, path: parent.pathChunks, dir: false, name: leaf.filename }
					});
				}
			}, 1);
			if (children.length === 0) return [];

			return [{
				k: type.k,
				v: type.v,
				l: 0,
				collapsed: true,
				folder: BrowserAdapter.folderOptions
			}, ...children];
		});
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		const thread = node.properties?.thread as Thread;
		const items: MenuItem[] = [];
		if (thread && this.host) {
			items.push({key: 'open', value: msg('Open'), icon: 'book-open'});
			if (thread.accounting === rp.accountingProvider.a) {
				items.push({key: 'properties', value: msg('Properties'), icon: 'pen-to-square'});
				items.push([
					{key: 'disable', value: msg('Disable'), icon: 'circle-xmark'},
					{key: 'hide', value: msg('Hide'), icon: 'eye-slash'},
					{key: 'enable', value: msg('Enable'), icon: 'eye'}
				][thread.enabled ?? 0]);
				if (thread.type === 'O') { // only for export threads
					items.push({key: 'apiLink', value: msg('API'), icon: 'link'});
				}
				items.push({key: 'access', value: msg('Access'), icon: 'shield-check'});
				items.push({key: 'duplicate', value: msg('Duplicate'), icon: 'copy'});
				items.push({key: 'delete', value: msg('Delete'), icon: 'trash', color: '#ff0000'});
			}
		}
		return items;
	}

	async onAction(key: string, node: NodeModel) {
		const thread = node.properties.thread as Thread;
		switch (key) {
			case 'open':
				this.host.open(node.key, {title: node.value});
				break;
			case 'apiLink':
				this.host.openModalSidebar({
					id: `etl-threadApiLinkForm-${thread.id_thread}`,
					caption: `${msg('API')}: ${thread.description}`,
					width: 400
				});
				break;
			case 'enable':
				thread.enabled = 1;
				await this.etlClient.post<Thread>(`threads`, thread);
				break;
			case 'hide':
				thread.enabled = 0;
				await this.etlClient.post<Thread>(`threads`, thread);
				break;
			case 'disable':
				thread.enabled = 2;
				await this.etlClient.post<Thread>(`threads`, thread);
				break;
			case 'properties':
				this.host.openModalSidebar({
					id: `etl-threadForm-${thread.id_thread}`,
					caption: `#${thread.id_thread} ${thread.description}`
				});
				break;
			case 'duplicate':
				this.host.openModalSidebar({
					id: `etl-threadForm-${thread.id_thread}-copy`,
					caption: `${msg('Copy')}: #${thread.id_thread} ${thread.description}`
				});
				break;
			case 'access':
				rp.host.open(`users-entityUsers-${EntityAccessType.Thread}-${thread.id_thread}`, { title: `${msg('Access')}: ${thread.description}` });
				break;
			case 'delete':
				if (thread) {
					const command = await this.host.openMenu({
						content: 'yes_no',
						text: `${msg('Are you sure you want to delete')} '#${thread.id_thread} ${thread.description}'?`
					});
					if (command?.id == 'Y') {
						await this.etlClient.delete(`threads/${thread.id_thread}`);
						this.browser.deleteNode(node);
					}
				}
				break;
		}
	}

	getTooltip(node: NodeModel) {
		const props = node?.properties;
		const thread = props?.thread as Thread;
		if (!thread) return null;
		if (thread.accounting !== rp.accountingProvider.a) return msg('Shared');

		return html`
			<table style="border-collapse: collapse;">
				<tr>
					<td>${msg('ID')}:</td>
					<td>${thread.id_thread ?? '-'}</td>
				</tr>
				<tr>
					<td>${msg('Name')}:</td>
					<td>${props.name ?? '-'}</td>
				</tr>
				<tr>
					<td>${msg('Order')}:</td>
					<td>${thread.ord_num ?? '-'}</td>
				</tr>
				${!thread.sync ? html`
					<tr>
						<td>${msg('Synchronization')}:</td>
						<td>${msg('Disabled')}</td>
					</tr>
				` : null}
				<tr>
					<td>${msg('Updated')}:</td>
					<td>${thread.date_created ? formatDate(thread.date_created) : '-'}</td>
				</tr>
				<tr>
					<td>${msg('User')}:</td>
					<td>${thread.who_created_dv ?? '-'}</td>
				</tr>
			</table>
		`;
	}

	getOptions(): Partial<TreeOptions> {
		return this.createOptions({
			contextMenuButton: true,
			selectMode: 'none',
			expanders: false,
			customStyle: css`
				.item-ord {
					flex: 0 0 auto;
					margin-left: 4px;
					font-size: 8px;
				}

				.item-flag {
					flex: 0 0 auto;
					margin-left: 4px;
					font-size: 10px;
				}

				.item-nosync {
					color: var(--og-colortype-red-background-color);
				}
			`,
			nodeRenderer: (div, node) => {
				const thread = node.properties?.thread as Thread;
				if (!thread) {
					div.textContent = node.value;
					return;
				}

				div.style.display = 'flex';
				div.style.flexDirection = 'row';

				render(html`
					<div style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">${node.value}</div>
					${thread.app_lock ? html`<og-icon class="item-flag" icon="lock"></og-icon>` : ''}
					${!thread.sync && thread.accounting === rp.accountingProvider.a ? html`<og-icon class="item-flag item-nosync" icon="arrows-rotate"></og-icon>` : ''}
					${thread.ord_num > 0 ? html`<div class="item-ord">${thread.ord_num}</div>` : ''}
				`, div);
			},
			afterRenderer: (div, node) => {
				const thread = node.properties?.thread as Thread;
				div.style.borderLeft = thread
					? `3px solid var(--og-colortype-${['yellow', 'green', 'red'][thread.enabled]}-background-color)`
					: 'none'
			},
		});
	}
}
